<template>
    <!-- Calendario del usuario property (/calendario). Diseño de Mi cuenta
         (bkCuenta.css + cuentaMenu.css) + propertyPanel.css (prefijo pp-):
         lateral con título, cifras, contacto y sus alojamientos; a la derecha
         las tarjetas (paso 1) o el calendario del elegido (paso 2).
         Datos de siempre: GET apiUrls.propertyCalendar (solo_alojamientos=1 y
         después idalojamiento + desde/hasta, doce meses de una vez). -->
    <div class="pp-page">
        <div class="bk pp">
            <aside class="bk-side pp-side">
                <h1 class="bk-title">{{ $t('property-calendar.title') }} <span class="bk-grad">{{ $t('property-calendar.title-accent') }}</span></h1>
                <p class="bk-subtitle">{{ $t('property-calendar.pick-hint') }}</p>

                <div class="pp-stats" aria-live="polite">
                    <div class="pp-stat"><span class="pp-stat-n">{{ cargando ? '·' : alojamientos.length }}</span><span class="pp-stat-k">{{ $t('property_panel.cal.stat_units') }}</span></div>
                    <template v-if="alojamientoSel && !cargandoCalendario">
                        <div class="pp-stat pp-stat--busy"><span class="pp-stat-n">{{ resumenMes.ocupados }}</span><span class="pp-stat-k">{{ $t('property_panel.cal.stat_busy') }}</span></div>
                        <div class="pp-stat pp-stat--free"><span class="pp-stat-n">{{ resumenMes.libres }}</span><span class="pp-stat-k">{{ $t('property_panel.cal.stat_free') }}</span></div>
                    </template>
                </div>

                <div class="pp-contact"><PropertyContactButton /></div>

                <!-- Con uno elegido: la lista para cambiar rápido (en escritorio) -->
                <nav v-if="alojamientoSel && alojamientos.length > 1" class="bk-snav pp-units" :aria-label="$t('property_panel.cal.units_label')">
                    <p class="bk-snav-title">{{ $t('property_panel.cal.units_label') }}</p>
                    <ul class="bk-snav-list">
                        <li v-for="a in alojamientos" :key="a.idalojamiento">
                            <button type="button" :class="['bk-snav-link', 'pp-unit-link', { 'is-active': a.idalojamiento === alojamientoSel.idalojamiento }]" :aria-current="a.idalojamiento === alojamientoSel.idalojamiento ? 'true' : null" @click="elegir(a)">
                                <span class="pp-unit-txt">{{ a.direccion || nombreDe(a) }}</span>
                                <em v-if="a.idcodigo" class="pp-code">{{ a.idcodigo }}</em>
                            </button>
                        </li>
                    </ul>
                </nav>
            </aside>

            <section class="pp-main">
                <!-- ───── Paso 1: elegir alojamiento ───── -->
                <template v-if="!alojamientoSel">
                    <div v-if="!cargando && alojamientos.length" class="pp-toolbar">
                        <label class="pp-search">
                            <span aria-hidden="true" v-html="IC.search"></span>
                            <span class="pp-sr">{{ $t('property-calendar.search') }}</span>
                            <input v-model="busqueda" type="search" autocomplete="off" :placeholder="$t('property-calendar.search')" />
                        </label>
                        <span class="pp-count">{{ alojamientosFiltrados.length }} {{ $tc('property-calendar.units', alojamientosFiltrados.length) }}</span>
                    </div>

                    <!-- Primera carga: solo el header y la pantalla de carga (sin esqueletos ni footer) -->
                    <HyFLoader v-if="cargando" variant="inline" />
                    <div v-else-if="error" class="pp-empty pp-empty--error" role="alert"><span aria-hidden="true" v-html="IC.warn"></span><p>{{ error }}</p></div>
                    <div v-else-if="!alojamientos.length" class="pp-empty"><span aria-hidden="true" v-html="IC.building"></span><p>{{ $t('property-calendar.empty') }}</p></div>
                    <div v-else-if="!alojamientosFiltrados.length" class="pp-empty"><span aria-hidden="true" v-html="IC.search"></span><p>{{ $t('property-calendar.no-matches') }}</p></div>

                    <ul v-else class="pp-cards">
                        <li v-for="a in alojamientosFiltrados" :key="a.idalojamiento">
                            <button type="button" class="pp-card" @click="elegir(a)">
                                <span class="pp-card-photo">
                                    <img v-if="fotoDe(a)" :src="fotoDe(a)" alt="" loading="lazy" />
                                    <span v-else class="pp-photo-empty" aria-hidden="true" v-html="IC.building"></span>
                                    <span v-if="a.idcodigo" class="pp-card-code">{{ $t('property_panel.cal.code', { c: a.idcodigo }) }}</span>
                                </span>
                                <span class="pp-card-body">
                                    <b class="pp-card-addr">{{ a.direccion || nombreDe(a) }}</b>
                                    <span class="pp-card-cta">{{ $t('property-calendar.see-calendar') }}<span aria-hidden="true" v-html="IC.next"></span></span>
                                </span>
                            </button>
                        </li>
                    </ul>
                </template>

                <!-- ───── Paso 2: calendario ───── -->
                <template v-else>
                    <div class="pp-unit">
                        <button type="button" class="bk-btn bk-btn--ghost pp-back" @click="volver"><span aria-hidden="true" v-html="IC.back"></span>{{ $t('property_panel.cal.all') }}</button>
                        <span class="pp-unit-photo">
                            <img v-if="fotoDe(alojamientoSel)" :src="fotoDe(alojamientoSel)" alt="" />
                            <span v-else class="pp-photo-empty" aria-hidden="true" v-html="IC.building"></span>
                        </span>
                        <div class="pp-unit-body">
                            <h2>{{ alojamientoSel.direccion || nombreDe(alojamientoSel) }}</h2>
                            <span v-if="alojamientoSel.idcodigo" class="pp-code">{{ $t('property_panel.cal.code', { c: alojamientoSel.idcodigo }) }}</span>
                        </div>
                    </div>

                    <div v-if="cargandoCalendario" class="pp-loading"><HyFLoader variant="inline" /></div>
                    <div v-else-if="error" class="pp-empty pp-empty--error" role="alert"><span aria-hidden="true" v-html="IC.warn"></span><p>{{ error }}</p></div>

                    <section v-else class="pp-board" :aria-label="rangoVisible">
                        <header class="pp-board-head">
                            <div>
                                <h2>{{ rangoVisible }}</h2>
                                <p class="pp-board-sum">
                                    <span class="pp-dot pp-dot--busy" aria-hidden="true"></span>{{ $tc('property_panel.cal.busy', resumenVista.ocupados, { n: resumenVista.ocupados }) }}
                                    <span class="pp-dot pp-dot--free" aria-hidden="true"></span>{{ $tc('property_panel.cal.free', resumenVista.libres, { n: resumenVista.libres }) }}
                                </p>
                            </div>
                            <div class="pp-controls">
                                <button type="button" class="bk-btn bk-btn--ghost pp-today" :disabled="offset === 0" @click="irHoy">{{ $t('property-calendar.today') }}</button>
                                <div class="pp-arrows">
                                    <button type="button" :disabled="!puedeRetroceder" :aria-label="$t('property-calendar.prev-month')" @click="moverMes(-1)"><span aria-hidden="true" v-html="IC.back"></span></button>
                                    <button type="button" :disabled="!puedeAvanzar" :aria-label="$t('property-calendar.next-month')" @click="moverMes(1)"><span aria-hidden="true" v-html="IC.next"></span></button>
                                </div>
                            </div>
                        </header>

                        <transition-group :name="'pp-slide-' + direccion" tag="div" class="pp-months">
                            <div v-for="mes in mesesVisibles" :key="mes.clave" class="pp-month">
                                <h3 class="pp-month-t">{{ mes.nombre }} {{ mes.anio }}</h3>
                                <div class="pp-weekdays" aria-hidden="true"><span v-for="d in diasSemana" :key="d">{{ d }}</span></div>
                                <div class="pp-grid" role="grid" :aria-label="mes.nombre + ' ' + mes.anio">
                                    <span v-for="celda in mes.celdas" :key="celda.clave" :class="['pp-day', celda.clases]" role="gridcell" :title="celda.titulo" :aria-label="celda.titulo">
                                        <span v-if="celda.dia" class="pp-day-in">{{ celda.dia }}</span>
                                    </span>
                                </div>
                            </div>
                        </transition-group>

                        <ul class="pp-legend">
                            <li><span class="pp-swatch pp-swatch--free" aria-hidden="true"></span>{{ $t('property-calendar.legend-free') }}</li>
                            <li><span class="pp-swatch pp-swatch--busy" aria-hidden="true"></span>{{ $t('property-calendar.legend-busy') }}</li>
                            <li><span class="pp-swatch pp-swatch--today" aria-hidden="true"></span>{{ $t('property-calendar.today') }}</li>
                        </ul>
                    </section>
                </template>
            </section>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import apiUrls from '@/new-constants/apiUrls';
import HyFLoader from '@/components/Loader/HyFLoader.vue';
import PropertyContactButton from '@/components/Property/PropertyContactButton.vue';
import ICON from '@/components/Cuenta/iconos';

/** Meses que se piden al backend (desde el mes en curso). */
const MESES_CARGADOS = 12;
const P = d => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
// Íconos: texto fijo de este archivo, nunca datos del usuario.
const IC = {
    back: P('<path d="M15 18l-6-6 6-6"/>'),
    next: P('<path d="m9 18 6-6-6-6"/>'),
    warn: P('<path d="M12 3 2 20h20z"/><path d="M12 10v4M12 17h.01"/>'),
    search: ICON.search, building: ICON.building,
};

/**
 * Calendario de ocupación del usuario tipo property, en dos pasos:
 *  1. la lista de sus alojamientos, para elegir cuál mirar;
 *  2. el calendario de ese alojamiento: rojo si el día está ocupado (reserva o
 *     bloqueo) y verde si está libre; los días seguidos de una misma estadía
 *     se unen en una barra.
 * La ocupación se pide recién en el paso 2, solo del elegido y de una vez los
 * doce meses: navegar entre meses no hace más pedidos.
 */
export default {
    name: 'PropertyCalendar',
    components: { HyFLoader, PropertyContactButton },
    data() {
        return {
            IC,
            alojamientos: [],
            alojamientoSel: null,
            busqueda: '',
            reservas: [],
            bloqueos: [],
            // Rango efectivamente cargado (lo confirma el backend, que lo topea).
            rangoDesde: null,
            rangoHasta: null,
            cargando: true,
            cargandoCalendario: false,
            error: null,
            offset: 0,
            direccion: 'next',
            // 2 meses lado a lado en escritorio, 1 en celular.
            mesesPorVista: 2,
        };
    },
    computed: {
        alojamientosFiltrados() {
            const q = this.busqueda.trim().toLowerCase();
            if (!q) return this.alojamientos;
            return this.alojamientos.filter(a => [this.nombreDe(a), a.direccion, a.idcodigo, a.idalojamiento].join(' ').toLowerCase().indexOf(q) !== -1);
        },
        /**
         * Qué ocupa cada día: { 'YYYY-MM-DD': { tipo, id } }. `id` identifica la
         * estadía o el bloqueo, para unir en barra solo los días de una misma.
         * La reserva gana sobre el bloqueo si caen el mismo día.
         */
        ocupacionPorDia() {
            const dias = {};
            const marcar = (desde, hasta, tipo, id) => {
                if (!desde || !hasta) return;
                const limiteIni = this.rangoDesde || desde;
                const limiteFin = this.rangoHasta || hasta;
                let cursor = desde < limiteIni ? limiteIni : desde;
                const fin = hasta > limiteFin ? limiteFin : hasta;
                let guarda = 0;
                while (cursor <= fin && guarda < 420) {
                    if (tipo === 'reserva' || !dias[cursor]) dias[cursor] = { tipo, id: `${tipo}-${id}` };
                    cursor = this.sumarDia(cursor);
                    guarda++;
                }
            };
            this.bloqueos.forEach(b => marcar(b.fecha_inicio, b.fecha_termino, 'bloqueo', b.idrestriccion));
            this.reservas.forEach(r => marcar(r.fecha_inicio, r.fecha_termino, 'reserva', r.idreserva));
            return dias;
        },
        diasSemana() {
            // Semana de lunes a domingo. 2024-01-01 fue lunes.
            const nombres = [];
            for (let i = 0; i < 7; i++) nombres.push(new Date(2024, 0, 1 + i).toLocaleDateString(this.$i18n.locale, { weekday: 'short' }).replace('.', ''));
            return nombres;
        },
        mesesVisibles() {
            const hoy = new Date();
            const meses = [];
            for (let i = 0; i < this.mesesPorVista; i++) meses.push(this.armarMes(new Date(hoy.getFullYear(), hoy.getMonth() + this.offset + i, 1)));
            return meses;
        },
        rangoVisible() {
            const m = this.mesesVisibles;
            if (!m.length) return '';
            const a = m[0], b = m[m.length - 1];
            if (a === b) return `${a.nombre} ${a.anio}`;
            if (a.anio === b.anio) return `${a.nombre} – ${b.nombre} ${b.anio}`;
            return `${a.nombre} ${a.anio} – ${b.nombre} ${b.anio}`;
        },
        // Días ocupados y libres del mes en curso (cifras del lateral)…
        resumenMes() { return this.contar(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 1); },
        // …y de lo que se está viendo.
        resumenVista() {
            const hoy = new Date();
            return this.contar(new Date(hoy.getFullYear(), hoy.getMonth() + this.offset, 1), this.mesesPorVista);
        },
        puedeRetroceder() { return this.offset > 0; },
        puedeAvanzar() { return this.offset + this.mesesPorVista < MESES_CARGADOS; },
    },
    methods: {
        /** Paso 1: solo la lista, sin ocupación. */
        async cargarAlojamientos() {
            this.cargando = true;
            this.error = null;
            try {
                const { data } = await axios.get(apiUrls.propertyCalendar, { params: { solo_alojamientos: 1 } });
                this.alojamientos = data.alojamientos || [];
            } catch (e) {
                console.error('[PropertyCalendar] no se pudieron cargar los alojamientos:', e);
                this.error = this.$t('property-calendar.error');
            } finally {
                this.cargando = false;
            }
        },
        /** Paso 2: la ocupación del alojamiento elegido, doce meses. */
        async cargarCalendario() {
            if (!this.alojamientoSel) return;
            const id = this.alojamientoSel.idalojamiento;
            this.cargandoCalendario = true;
            this.error = null;
            const hoy = new Date();
            const desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
            const hasta = new Date(hoy.getFullYear(), hoy.getMonth() + MESES_CARGADOS, 0);
            try {
                const { data } = await axios.get(apiUrls.propertyCalendar, { params: { idalojamiento: id, desde: this.aIso(desde), hasta: this.aIso(hasta) } });
                if (!this.alojamientoSel || this.alojamientoSel.idalojamiento !== id) return;
                this.reservas = data.reservas || [];
                this.bloqueos = data.bloqueos || [];
                this.rangoDesde = data.desde || null;
                this.rangoHasta = data.hasta || null;
            } catch (e) {
                console.error('[PropertyCalendar] no se pudo cargar el calendario:', e);
                this.error = this.$t('property-calendar.error');
            } finally {
                this.cargandoCalendario = false;
            }
        },
        elegir(a) {
            if (this.alojamientoSel && this.alojamientoSel.idalojamiento === a.idalojamiento) return;
            this.alojamientoSel = a;
            this.reservas = [];
            this.bloqueos = [];
            this.offset = 0;
            this.cargarCalendario();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        volver() {
            this.alojamientoSel = null;
            this.reservas = [];
            this.bloqueos = [];
            this.error = null;
        },
        irHoy() { this.direccion = 'prev'; this.offset = 0; },
        moverMes(delta) {
            const max = MESES_CARGADOS - this.mesesPorVista;
            if (delta) this.direccion = delta > 0 ? 'next' : 'prev';
            this.offset = Math.min(Math.max(this.offset + delta, 0), max);
        },
        ajustarVista() {
            this.mesesPorVista = window.innerWidth >= 1200 ? 2 : 1;
            this.moverMes(0);
        },
        // Días ocupados / libres desde el primer día de un mes, por N meses
        // (solo los que caen dentro de lo cargado).
        contar(primero, meses) {
            let ocupados = 0, libres = 0;
            const hasta = new Date(primero.getFullYear(), primero.getMonth() + meses, 0);
            for (let d = new Date(primero); d <= hasta; d.setDate(d.getDate() + 1)) {
                if (this.ocupacionPorDia[this.aIso(d)]) ocupados++; else libres++;
            }
            return { ocupados, libres };
        },
        /**
         * Celdas de un mes, con huecos al principio para arrancar en lunes. Los
         * días ocupados seguidos se unen en una barra (lunes y domingo la cortan).
         */
        armarMes(primero) {
            const anio = primero.getFullYear();
            const mes = primero.getMonth();
            const diasEnMes = new Date(anio, mes + 1, 0).getDate();
            const hueco = (primero.getDay() + 6) % 7;
            const hoyIso = this.aIso(new Date());
            const nombre = primero.toLocaleDateString(this.$i18n.locale, { month: 'long' });
            const celdas = [];
            for (let i = 0; i < hueco; i++) celdas.push({ clave: `h-${anio}-${mes}-${i}`, dia: null, clases: 'pp-day--blank' });
            for (let d = 1; d <= diasEnMes; d++) {
                const iso = this.aIso(new Date(anio, mes, d));
                const ocupa = this.ocupacionPorDia[iso] || null;
                const columna = (hueco + d - 1) % 7;
                const clases = [];
                let tituloDia;
                if (ocupa) {
                    const ant = d > 1 ? this.ocupacionPorDia[this.aIso(new Date(anio, mes, d - 1))] : null;
                    const sig = d < diasEnMes ? this.ocupacionPorDia[this.aIso(new Date(anio, mes, d + 1))] : null;
                    clases.push('pp-day--busy');
                    if (ant && ant.id === ocupa.id && columna !== 0) clases.push('pp-day--join-l');
                    if (sig && sig.id === ocupa.id && columna !== 6) clases.push('pp-day--join-r');
                    tituloDia = this.$t('property-calendar.legend-busy');
                } else {
                    clases.push('pp-day--free');
                    tituloDia = this.$t('property-calendar.legend-free');
                }
                if (iso === hoyIso) clases.push('pp-day--today');
                celdas.push({ clave: iso, dia: d, clases: clases.join(' '), titulo: `${this.fechaCorta(iso)} · ${tituloDia}` });
            }
            return { clave: `${anio}-${mes}`, nombre: nombre.charAt(0).toUpperCase() + nombre.slice(1), anio, celdas };
        },
        fechaCorta(iso) {
            if (!iso) return '';
            const [y, m, d] = iso.split('-').map(Number);
            return new Date(y, m - 1, d).toLocaleDateString(this.$i18n.locale, { day: 'numeric', month: 'short' });
        },
        /**
         * Primera foto de la galería. Mismo armado de URL que el carrusel de Mis
         * alojamientos: las viejas guardan solo el nombre del archivo y las nuevas
         * la URL completa de S3.
         */
        fotoDe(a) {
            const galeria = (a && a.gallery) || [];
            if (!galeria.length) return '';
            const url = galeria[0].url || '';
            if (!url) return '';
            if (url.indexOf('http') === -1) return (process.env.VUE_APP_S3_URL || '') + url;
            const bucket = (process.env.VUE_APP_AWS_BUCKET_URL || '').replace(/^\/+|\/+$/g, '');
            if (!bucket || url.indexOf('s3-sa-east-1.amazonaws.com') === -1 || url.indexOf(bucket) > -1) return url;
            try {
                const parsed = new URL(url);
                parsed.pathname = `/${bucket}/${parsed.pathname.replace(/^\/+/, '')}`;
                return parsed.toString();
            } catch (e) {
                return url;
            }
        },
        /** El nombre viene como JSON con un idioma por clave (o texto plano). */
        nombreDe(a) {
            if (!a) return '';
            const crudo = a.nombre;
            if (crudo && typeof crudo === 'object') return crudo[this.$i18n.locale] || crudo.es || Object.values(crudo)[0] || a.direccion;
            if (typeof crudo === 'string' && crudo.trim().charAt(0) === '{') {
                try {
                    const parsed = JSON.parse(crudo);
                    return parsed[this.$i18n.locale] || parsed.es || Object.values(parsed)[0] || a.direccion;
                } catch (e) {
                    return crudo;
                }
            }
            return crudo || a.direccion || `#${a.idalojamiento}`;
        },
        aIso(f) {
            return `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}-${String(f.getDate()).padStart(2, '0')}`;
        },
        sumarDia(iso) {
            const [y, m, d] = iso.split('-').map(Number);
            return this.aIso(new Date(y, m - 1, d + 1));
        },
    },
    created() {
        this.cargarAlojamientos();
    },
    mounted() {
        this.ajustarVista();
        window.addEventListener('resize', this.ajustarVista);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.ajustarVista);
    },
};
</script>

<style src="../../components/BankInformation/bkCuenta.css"></style>
<style src="../../components/Cuenta/cuentaMenu.css"></style>
<style src="../../components/Property/propertyPanel.css"></style>
